import styled, { css } from 'styled-components';
import { Link as LinkComponent } from 'react-router-dom';
import { media, remCalc, layout } from '@prisjakt/ui-base';
import { Tooltip as TooltipComponent } from '@prisjakt/ui/tooltip';
import { DropdownToggle } from '@prisjakt/ui/dropdown';
import {
  ButtonText as ButtonTextComponent,
  TonalButton,
} from '@prisjakt/ui/button/variants';
import { zindex } from 'src/ui/zindex';
import { ProductCoreProperties } from 'src/components/core-properties';
import { ListItem as DropdownItemComponent } from 'src/components/dropdown-new/item';
import CampaignBadgeComponent from 'src/components/campaign-badge';

import { SpecialPageTitle } from '../../styles';

export const DesktopHeader = styled.header`
  display: none;

  ${media.mediumUp`
    flex-flow: row wrap;
    margin: ${layout.measurements.spacing050} ${
    layout.measurements.spacing050
  } 0 ${layout.measurements.spacing050};
    display: flex;
    position: relative;
    padding-bottom: ${layout.measurements.spacing050};
    border-bottom: 1px solid ${p => p.theme.lines.primary};
  `}

  ${media.mediumDown`
    padding-left: ${layout.measurements.spacing050};
    padding-right: ${layout.measurements.spacing050};
  `}
`;

export const ProductSummary = styled.div`
  /* width is the same as price history graph on desktop.
  It's set to keep up space while rendering. */
  width: 310px;
  flex: 0 0 auto;

  ${media.mediumDown`
    width: auto;
  `}
`;

type TitleProps = {
  inner?: boolean;
  outer?: boolean;
};
export const Title = styled(SpecialPageTitle)<TitleProps>`
  margin-bottom: ${layout.measurements.spacing050};

  ${props =>
    props.inner &&
    css`
      margin-right: 20px;
    `}

  ${props =>
    props.outer &&
    css`
      flex: 1 0 auto;
      margin-bottom: ${layout.measurements.spacing050};
      margin-right: 0px;
    `}
`;

export const More = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const Content = styled.div`
  flex: 1 1;
  justify-content: space-between;
  padding-right: ${layout.measurements.spacing050};
  padding-left: ${layout.measurements.spacing050};
  min-width: 0;
`;

export const Media = styled.div`
  position: relative;
  margin-right: ${layout.measurements.spacing100};
`;

export const StoreLogo = styled(LinkComponent)`
  position: absolute;
  text-align: center;
  bottom: calc(100% + 2px);
  right: 10px;

  img {
    max-width: 88px;
  }
`;

export const CategoryRank = styled.div`
  margin-bottom: ${remCalc(10)};
  color: ${p => p.theme.colors.base600};
`;

export const StyledRating = styled.div`
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

export const WriteReview = styled.div`
  cursor: pointer;
  color: ${p => p.theme.text.linkExternal};

  &:hover,
  &:focus {
    color: ${p => p.theme.text.linkExternalHover};
    text-decoration: underline;
  }
`;

export const RatingWrapper = styled.div`
  margin-bottom: ${remCalc(10)};
  text-decoration: underline;
  color: ${p => p.theme.text.callToAction};

  span {
    color: inherit;
  }
  &:hover span {
    color: ${p => p.theme.text.callToActionHover};
  }
`;

export const DisplayProperties = styled(ProductCoreProperties)`
  margin: 0 0 ${layout.measurements.spacing050};
`;

export const ReviewBadges = styled.div`
  margin: 0 0 ${layout.measurements.spacing050};
`;

export const Link = styled(LinkComponent)`
  color: ${p => p.theme.colors.maybeBlue700};
  cursor: pointer;
  outline: none;

  &:hover,
  &:focus {
    color: ${p => p.theme.colors.maybeBlue500};
    text-decoration: underline;
  }
`;

export const RankContainer = styled.div`
  display: flex;
  flex-wrap: wrap;

  > :not(:last-child) {
    margin-right: ${layout.measurements.spacing050};
  }
`;

export const CampaignBadge = styled(CampaignBadgeComponent)`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

export const TabletTitle = styled.div`
  display: none;

  ${media.mediumDown`
    display: block;
  `}
`;

export const DesktopTitle = styled.div`
  display: none;

  ${media.largeUp`
    display: block;
  `}
`;

// Action buttons

const sharedButtonStyling = css`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 100%;
`;

export const Button = styled(TonalButton)`
  ${sharedButtonStyling};
`;

export const ProductActionButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: ${layout.measurements.spacing050};

  ${media.mediumOnly`
  grid-template-columns: 1fr;
`}
`;

export const Tooltip = styled(TooltipComponent).attrs({ minWidth: '180px' })`
  z-index: ${zindex.menu.sticky.background.isSticky + 1};
  flex: 0;
`;

export const ButtonText = styled(ButtonTextComponent).attrs({
  'data-test': 'ButtonText',
})`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: none;

  ${media.largeUp`
    display: inline;
  `}
`;

export const DropdownItem = styled(DropdownItemComponent)`
  max-width: 200px;
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: ${layout.measurements.spacing050} ${layout.measurements.spacing100};
`;

export const DropdownToggleButton = styled(DropdownToggle)`
  && {
    ${sharedButtonStyling};
  }
`;
